/** Arquivo: src/components/BalanceStrip.tsx | Versão: 4.13.0 | Data: 17/09/2026 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Landmark, ReceiptText, TrendingUp } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import MobileActionSheet from './MobileActionSheet'
import { calcularSaldosContas, calcularSaldosInvestimentos } from '../services/financeiro'
import type { ContaComSaldo, InvestimentoComSaldo } from '../types/database'

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

type ItemSaldo = ContaComSaldo | InvestimentoComSaldo

export default function BalanceStrip({ baseId, incluirInvestimentos = false }: { baseId?: string | null; incluirInvestimentos?: boolean }) {
  const navigate = useNavigate()
  const [selecionado, setSelecionado] = useState<{ tipo: 'contas' | 'investimentos'; item: ItemSaldo } | null>(null)
  const [contas, setContas] = useState<ContaComSaldo[]>([])
  const [investimentos, setInvestimentos] = useState<InvestimentoComSaldo[]>([])
  const [erro, setErro] = useState('')

  useEffect(() => {
    if (!baseId) return
    let ativo = true

    ;(async () => {
      try {
        const [c, i] = await Promise.all([
          calcularSaldosContas(baseId),
          incluirInvestimentos ? calcularSaldosInvestimentos(baseId) : Promise.resolve([]),
        ])
        if (ativo) {
          setContas(c.filter(x => x.ativa !== false))
          setInvestimentos(i.filter(x => String(x.status || '').toUpperCase() !== 'INATIVO'))
        }
      } catch (e) {
        if (ativo) setErro(e instanceof Error ? e.message : 'Erro ao calcular saldos.')
      }
    })()

    return () => { ativo = false }
  }, [baseId, incluirInvestimentos])

  const totalContas = useMemo(() => contas.reduce((s, c) => s + Number(c.saldo_atual || 0), 0), [contas])
  const totalInvest = useMemo(() => investimentos.reduce((s, i) => s + Number(i.saldo_atual || 0), 0), [investimentos])

  if (!baseId) return null

  const bloco = (tipo: 'contas' | 'investimentos') => {
    const itens: ItemSaldo[] = tipo === 'contas' ? contas : investimentos
    const total = tipo === 'contas' ? totalContas : totalInvest
    const Icon = tipo === 'contas' ? Landmark : TrendingUp

    return (
      <section className={`balance-strip balance-equation-strip balance-strip-${tipo}`}>
        <div className="balance-equation-head">
          <span className="balance-equation-icon"><Icon size={18} /></span>
          <div className="balance-equation-title">
            <strong>{tipo === 'contas' ? 'Saldos das contas' : 'Investimentos'}</strong>
            <small>{tipo === 'contas' ? 'Soma dos saldos por instituição' : 'Soma das posições por instituição'}</small>
          </div>
        </div>

        <div className="balance-equation-row" role="group" aria-label={tipo === 'contas' ? 'Soma dos saldos das contas' : 'Soma dos investimentos'}>
          {itens.map((x, index) => {
            const saldo = Number(x.saldo_atual || 0)
            const nome = String(x.instituicao || x.nome || 'Conta')
            return (
              <Fragment key={x.id}>
                <button type="button" className={`balance-equation-term ${saldo < 0 ? 'is-negative' : saldo > 0 ? 'is-positive' : 'is-zero'}`} onClick={() => setSelecionado({ tipo, item: x })} aria-label={`Ações para ${x.nome || nome}`}>
                  <strong className="privacy-value">{brl.format(saldo)}</strong>
                  <small title={nome}>{nome}</small>
                </button>
                {index < itens.length - 1 && <span className="balance-equation-operator" aria-hidden="true">+</span>}
              </Fragment>
            )
          })}
          <span className="balance-equation-operator is-equals" aria-hidden="true">=</span>
          <button type="button" className="balance-equation-term balance-equation-total" onClick={() => navigate(tipo === 'contas' ? '/contas' : '/investimentos')} aria-label={tipo === 'contas' ? 'Consultar todas as contas' : 'Consultar todos os investimentos'}>
            <strong className="privacy-value">{brl.format(total)}</strong>
            <small>Consolidado</small>
          </button>
        </div>
      </section>
    )
  }

  return (
    <div className="balance-strip-wrap">
      {bloco('contas')}
      {incluirInvestimentos && bloco('investimentos')}
      {erro && <div className="balance-error">{erro}</div>}
      <MobileActionSheet aberto={!!selecionado} titulo={selecionado?.item.nome || 'Saldo'} subtitulo={selecionado?.item.instituicao || undefined} onClose={() => setSelecionado(null)} actions={selecionado ? [{
        label: 'Ver extrato', icon: <ReceiptText size={18}/>,
        onClick: () => navigate(`/${selecionado.tipo}?extrato=${encodeURIComponent(selecionado.item.id)}`),
      }] : []}/>
    </div>
  )
}

